import type { ReactNode } from "react";

import { GlobeIcon, MailIcon, PhoneIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { belowComputer } from "simplydo/react/theme";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  XIcon,
} from "simplydo/react/ui/brand-icon";
import { H1 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import { companyPlaces } from "components/innointel/Tools/locations";

import { InfoBox, InfoBoxWidget, InfoContainer, KeyFact } from "../Styles";
import CompanyLocations from "./CompanyLocations";
import { SicCodeList } from "./SicCodeList";

const LocationColumn = styled.div`
  flex: 0 0 min(340px, 42%);
  min-width: 0;

  ${belowComputer} {
    flex: 1 1 auto;
  }
`;

const ContactList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
  line-height: 20px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex: none;
    color: #5b6470;
  }

  a,
  span {
    min-width: 0;
    color: #1f2328;
    overflow-wrap: anywhere;
  }

  a {
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/** A link without its protocol, "www." or trailing slash, e.g. "x.com/compositesuk". */
const shortUrl = (url: string) =>
  url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

type CompanyBasicInfoProps = {
  company: OpenAPI.Schemas["InnointelCompanies"];
  locationNote?: ReactNode;
};

const CompanyBasicInfo = ({ company, locationNote }: CompanyBasicInfoProps) => {
  const {
    // Contact info
    phone,
    email,
    website,
    twitter,
    facebook,
    linkedin,
    instagram,
    companiesHouse = {},
    // Other
    description,
    foundedOn,
    patents = {},
    companyNumber,
    ai,
  } = company;

  const keyFactsVisible =
    !!companyNumber ||
    (companiesHouse.sicCodes?.length ?? 0) > 0 ||
    !!patents?.total;

  const hasContact = !!(
    phone ||
    email ||
    website?.url ||
    twitter?.url ||
    facebook?.url ||
    linkedin?.url ||
    instagram?.url
  );
  const hasLocation = companyPlaces(company.location).length > 0;
  const contactRows = [
    phone
      ? { key: "phone", icon: <Icon icon={PhoneIcon} />, text: phone }
      : null,
    email
      ? {
          key: "email",
          icon: <Icon icon={MailIcon} />,
          text: email,
          href: `mailto:${email}`,
        }
      : null,
    website?.url
      ? {
          key: "website",
          icon: <Icon icon={GlobeIcon} />,
          text: shortUrl(website.url),
          href: website.url,
        }
      : null,
    twitter?.url
      ? {
          key: "twitter",
          icon: <XIcon size={15} />,
          text: shortUrl(twitter.url),
          href: twitter.url,
        }
      : null,
    facebook?.url
      ? {
          key: "facebook",
          icon: <FacebookIcon size={16} />,
          text: shortUrl(facebook.url),
          href: facebook.url,
        }
      : null,
    linkedin?.url
      ? {
          key: "linkedin",
          icon: <LinkedInIcon size={16} />,
          text: shortUrl(linkedin.url),
          href: linkedin.url,
        }
      : null,
    instagram?.url
      ? {
          key: "instagram",
          icon: <InstagramIcon size={16} />,
          text: shortUrl(instagram.url),
          href: instagram.url,
        }
      : null,
  ].filter((row) => row !== null);

  return (
    <InfoContainer>
      <InfoBox>
        <H1>About</H1>
        {foundedOn ? (
          <span className="meta">
            Established{" "}
            {foundedOn?.value
              ? `${formatDate(foundedOn.value, "MMM d, yyyy")} `
              : ""}
          </span>
        ) : null}
        {ai?.description || description}
        {hasContact ? (
          <ContactList>
            {contactRows.map((row) => (
              <li key={row.key}>
                <span className="icon">{row.icon}</span>
                {row.href ? (
                  <a
                    href={row.href}
                    target={row.key === "email" ? undefined : "_blank"}
                    rel="noreferrer noopener"
                  >
                    {row.text}
                  </a>
                ) : (
                  <span>{row.text}</span>
                )}
              </li>
            ))}
          </ContactList>
        ) : null}
        {keyFactsVisible && (
          <InfoBox>
            <div className="boxes" style={{ marginTop: 0 }}>
              <InfoBoxWidget $noBox>
                {/*
              <KeyFact
                title='DUNS verified'
                info={`Find out if this company is verified through Dun & Bradstreet. You may need to press the 'Request additional data' button below to find out this additional information`}
                fact={duns?.duns ? duns.primaryName : 'Not yet verified'}
                verified={duns?.duns}
              />
            */}
                {companyNumber ? (
                  <KeyFact
                    title="Registration number"
                    fact={
                      <a
                        href={`https://find-and-update.company-information.service.gov.uk/company/${companyNumber}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {companyNumber}
                      </a>
                    }
                  />
                ) : null}
                {(companiesHouse.sicCodes?.length ?? 0) > 0 ? (
                  <KeyFact
                    title="SIC Codes"
                    info="Standard Industrial Classification codes are what a company tells Companies House it does."
                    fact={
                      <SicCodeList
                        codes={companiesHouse.sicCodes ?? []}
                        descriptions={company.sicCodeDescriptions}
                      />
                    }
                  />
                ) : null}
                {patents?.total ? (
                  <KeyFact
                    title="Patents"
                    fact={`${patents.total} found for company`}
                  />
                ) : null}
              </InfoBoxWidget>
            </div>
          </InfoBox>
        )}
      </InfoBox>
      {hasLocation ? (
        <LocationColumn>
          <CompanyLocations company={company} searchNote={locationNote} />
        </LocationColumn>
      ) : null}
    </InfoContainer>
  );
};

export default CompanyBasicInfo;
